import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { apiService, API_BASE_URL } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Product {
  externalId: string;
  name: string;
  description: string;
  price: number;
  discountPrice: number;
  credits: number;
}

const CheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingOrder, setCreatingOrder] = useState<boolean>(false);
  const [capturingOrder, setCapturingOrder] = useState<boolean>(false);

  const paypalClientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;

  // Load product
  useEffect(() => {
    const loadProduct = async () => {
      if (!productId) {
        setError(t('checkout.errors.productIdMissing'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getCreditPackages();
        const foundProduct = response.packages.find(
          (p: any) => p.externalId === productId
        );

        if (!foundProduct) {
          setError(t('checkout.errors.productNotFound'));
          return;
        }

        setProduct(foundProduct);
      } catch (err: any) {
        console.error('Error loading product:', err);
        setError(err.message || t('checkout.errors.failedToLoadProduct'));
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, t]);

  // Create order on backend (called when PayPal button is clicked)
  const createOrderOnBackend = async (): Promise<string> => {
    if (!product) {
      throw new Error(t('checkout.errors.productNotFoundError'));
    }

    setCreatingOrder(true);
    setError(null);

    try {
      const tokenRaw = localStorage.getItem('auth_token');
      const token = tokenRaw ? JSON.parse(tokenRaw) : null;

      if (!token) {
        throw new Error(t('checkout.errors.authTokenNotFound'));
      }

      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
        body: JSON.stringify({
          creditPackageId: product.externalId,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: t('checkout.errors.failedToCreateOrder') }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const orderData = await response.json();

      // Return PayPal order ID for PayPal SDK
      return orderData.externalOrderId || orderData.id;
    } catch (err: any) {
      console.error('Error creating order:', err);
      setError(err.message || t('checkout.errors.failedToCreateOrder'));
      throw err;
    } finally {
      setCreatingOrder(false);
    }
  };

  // Capture order on backend (called after PayPal approval)
  const captureOrderOnBackend = async (orderId: string): Promise<void> => {
    setCapturingOrder(true);
    setError(null);

    try {
      const tokenRaw = localStorage.getItem('auth_token');
      const token = tokenRaw ? JSON.parse(tokenRaw) : null;

      if (!token) {
        throw new Error(t('checkout.errors.authTokenNotFound'));
      }

      const response = await fetch(`${API_BASE_URL}/orders/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
        body: JSON.stringify({
          externalOrderId: orderId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t('checkout.errors.failedToCapturePayment'),
        }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Refresh credits balance in header
      window.dispatchEvent(new Event('balance:refresh'));

      // Redirect to success page with product data
      navigate('/checkout/success', {
        state: {
          orderId,
          amount: product?.discountPrice ?? product?.price,
          currency: 'EUR',
          credits: product?.credits,
          packageName: product?.name,
        },
      });
    } catch (err: any) {
      console.error('Error capturing order:', err);
      setError(err.message || t('checkout.errors.failedToCapturePayment'));
      throw err;
    } finally {
      setCapturingOrder(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !product) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/shop')}
          sx={{ mb: 3 }}
        >
          {t('checkout.backToShop')}
        </Button>
        <Alert variant="filled" severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/shop')}
        sx={{ mb: 3 }}
      >
        {t('checkout.backToShop')}
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        {t('checkout.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('checkout.orderSummary')}
          </Typography>
          <Divider sx={{ my: 2 }} />

          {/* Product Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {product.name}
            </Typography>
            {product.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {product.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body1">
                <strong>{t('checkout.credits')}:</strong> {product.credits}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" color="primary" fontWeight="bold">
                €{product.discountPrice}
              </Typography>
              {product.price > product.discountPrice && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through' }}
                >
                  €{product.price}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">{t('checkout.total')}:</Typography>
            <Typography variant="h5" fontWeight="bold" color="primary">
              €{product.discountPrice}
            </Typography>
          </Box>

          {/* PayPal Buttons */}
          <PayPalScriptProvider
            options={{
              clientId: paypalClientId,
              currency: 'EUR',
              intent: 'capture',
            }}
          >
            <PayPalButtons
              style={{
                layout: 'vertical',
                color: 'blue',
                shape: 'rect',
                label: 'paypal',
              }}
              createOrder={async () => {
                return await createOrderOnBackend();
              }}
              onApprove={async data => {
                if (data.orderID) {
                  await captureOrderOnBackend(data.orderID);
                }
              }}
              onCancel={() => {
                setError(t('checkout.paymentCancelled'));
              }}
              onError={err => {
                console.error('PayPal error:', err);
                setError(
                  t('checkout.errors.paypalError', {
                    message: err?.message || 'Unknown error',
                  })
                );
              }}
              disabled={creatingOrder || capturingOrder}
            />
          </PayPalScriptProvider>

          {(creatingOrder || capturingOrder) && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {creatingOrder
                  ? t('checkout.creatingOrder')
                  : t('checkout.processingPayment')}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              to="/terms-of-use"
              style={{
                fontSize: '0.875rem',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              {t('checkout.legalInfoLink')}
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CheckoutPage;
